import React, {ReactElement, useEffect, useState} from 'react';
import {cx} from '@emotion/css';
import {useIntl} from 'react-intl';

import {ArrowIcon, Hamburger as CloseIcon} from '@fo/root/designSystem/atoms/Icons';
import {ReactComponent as TideHamburgerIcon} from '@fo/shared-images/tide/icons/tideHamburgerMenu.svg';
import {IconRotation, IconSize} from '@fo/root/designSystem/atoms/Icons/IconWrapper';
import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import FundingOptionsLogo from '@fo/designSystem/FundingOptionsLogo/FundingOptionsLogo';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {INavigation, TSectionWithLabel} from '@fo/designSystem/Navigation/types';
import NavigationSection from '@fo/designSystem/Navigation/NavigationSection/NavigationSection';
import NavigationUserMenu from '@fo/designSystem/Navigation/NavigationUserMenuMobile/NavigationUserMenuMobile';

import styles from './NavigationMobile.styles';

function NavigationMobile({
  loginMenuText,
  alternativeJourneyLink,
  sections,
  hideTideBranding,
}: INavigation): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [openSection, setOpenSection] = useState<TSectionWithLabel>();
  const {formatMessage} = useIntl();

  const handleCloseAllSections = () => {
    setOpenSection(undefined);
  };

  useEffect(() => {
    if (openSection) {
      const elementToScrollTo = document.querySelector(`[aria-label='${openSection.label}']`) as HTMLElement;
      if (elementToScrollTo) {
        window.scrollTo({
          top: elementToScrollTo.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }, [openSection]);

  return (
    <nav className={cx(styles.componentWrapper)} data-testid="ui.navigation.mobile">
      {/*
       * Top bar: Logo - CTA - Menu
       */}
      <section className={cx(styles.topBar)}>
        <div className={cx(styles.logoContainer)}>
          <DynamicLink href={getPageRoute('home')}>
            <FundingOptionsLogo className={cx(styles.logo)} hideTideBranding={hideTideBranding} />
          </DynamicLink>
        </div>
        <div className={cx(styles.hamburgerContainer)}>
          <Button
            onClick={() => {
              setIsMenuOpen((currentState) => !currentState);
            }}
            variant={ButtonVariant.UNSTYLED}
            ariaLabel={loginMenuText}
            testId="button.navigation.hamburger"
            className={cx(styles.hamburger)}
          >
            {isMenuOpen ? <CloseIcon size={IconSize.SMALL} /> : <TideHamburgerIcon />}
          </Button>
        </div>
      </section>
      {/*
       * Main bar: sections - CTA
       */}
      <section className={cx(styles.mainBar(isMenuOpen))}>
        {sections.map((section) => {
          // Used to hide the business services section on tsb partner page
          if (hideTideBranding && section.label.includes('Tide')) {
            return null;
          }
          return (
            <div key={section.label} className={cx(styles.sectionsContainer(openSection === section))}>
              <Button
                key={section.label}
                className={cx(styles.sectionLabelTrigger(openSection?.label === section.label))}
                ariaLabel={section.label}
                onClick={() => {
                  if (openSection !== section) {
                    setOpenSection(section);
                  } else {
                    setOpenSection(undefined);
                  }
                }}
                testId="ui.navigation.section.label"
                type="button"
                variant={ButtonVariant.UNSTYLED}
              >
                <span>{section.label}</span>
                <ArrowIcon
                  rotation={openSection === section ? IconRotation.CCW_90 : IconRotation.CW_90}
                  size={IconSize.MEDIUM}
                />
              </Button>
              {openSection === section && (
                <NavigationSection
                  key={`section-${openSection.label}`}
                  card={openSection.card}
                  col1={openSection.col1}
                  col2={openSection.col2}
                  col3={openSection.col3}
                />
              )}
            </div>
          );
        })}
        <Button
          className={cx(styles.alternativeJourney)}
          ariaLabel={alternativeJourneyLink.label}
          href={alternativeJourneyLink.url}
          testId="ui.navigation.alternativeJourney"
          type="button"
          variant={ButtonVariant.UNSTYLED}
        >
          <span>{alternativeJourneyLink.label}</span>
          <ArrowIcon />
        </Button>
        <div className={cx(styles.loginContainer)}>
          <NavigationUserMenu
            loginMenuText={loginMenuText}
            onMenuOpen={handleCloseAllSections}
            openSectionInParent={openSection}
          />
        </div>
      </section>
      {/*
       * Click outside and close
       */}
      {openSection && (
        <div
          className={cx(styles.closeMenuArea)}
          onClick={() => setOpenSection(undefined)}
          onKeyDown={() => {
            // noop
          }}
          role="button"
          tabIndex={-1}
          aria-label={formatMessage({id: 'phoenix.modal.close'})}
        />
      )}
    </nav>
  );
}

export default NavigationMobile;
